import React, { useMemo } from 'react';
import { Box, IconButton } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { Money } from '@uplift/ui-v2';
import { useL10n } from '@uplift/hooks';
import { InfoOutlined, CheckCircle } from '@mui/icons-material';
import { ProviderFinancePeriodItem } from '@uplift/api';
import { PaymentWaiverReasonEnum } from '@uplift/types';


export type PaidCellProps = {
  row: ProviderFinancePeriodItem;
  hidePaid?: boolean;
};


const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    width: '100%',
    gridGap: theme.spacing(0.5),
  },
  paidButton: {
    color: theme.palette.success.main,
    cursor: 'default',
    '&:hover': {
      backgroundColor: theme.palette.success.background,
    }
  },
  tooltip: {
    fontSize: theme.typography.body2.fontSize,
    top: theme.spacing(-1)
  },
  tooltipMargin: {
    marginLeft: theme.spacing(0.5)
  },
  tooltipPaid: {
    width: 'min-content'
  },
  tooltipWarning: {
    maxWidth: '240px',
  },
  strikethrough: {
    textDecoration: 'line-through'
  }
}));


export const PaidCell: React.FC<PaidCellProps> = ({ row, hidePaid = false }) => {
  const { classes, cx } = useStyles();
  const l10n = useL10n();

  const amount = useMemo(() => {
    if (row.payoutAmount) {
      return row.payoutAmount.toFixed(2);
    }
    return 0;
  }, [ row.payoutAmount ]);

  const isRemoved = useMemo(() => {
    if (row.payment && (row.payment.refundedAt && row.payment.paymentWaiverReason !== PaymentWaiverReasonEnum.refundedToClientWithoutProviderAgreement)) {
      return true;
    }
    return false;
  }, [ row.payment ]);

  if (row instanceof Object) {

    if (row.payoutAmount === null) {
      return (
        <Box className={classes.root}>
          <Typography>{l10n.finance.monthly.tbd}</Typography>
          <Tooltip
            arrow
            classes={{ tooltip: cx(classes.tooltip, classes.tooltipWarning) }}
            title={l10n.finance.monthly.payoutCouldNotBeCalculated}
            style={{ width: 'fit-content' }}
          >
            <IconButton>
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    if (isRemoved) {
      return (
        <Box display="flex" alignItems="center">
          <Box className={cx(classes.root, classes.strikethrough)}>
            <Money value={amount} />
          </Box>
          <Tooltip
            arrow
            classes={{ tooltip: cx(classes.tooltip, classes.tooltipMargin) }}
            title={(
              <Typography style={{ fontWeight: 400 }} variant="body2" color="inherit">
                Late fee was removed from your payout
              </Typography>
            )}
          >
            <IconButton className={classes.tooltipMargin}>
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    if (row.paid && !hidePaid) {
      return (
        <Box className={cx(classes.root, { [classes.strikethrough]: isRemoved })}>
          <Box display="flex" flexDirection="column">
            <span><Money value={amount} /></span>
          </Box>
          <Tooltip
            arrow
            classes={{ tooltip: cx(classes.tooltip, classes.tooltipPaid) }}
            title={(
              <span>
                <span style={{ whiteSpace: 'nowrap' }}>{l10n.finance.monthly.paymentInitiatedOn(row.payoutDate)}</span> {l10n.finance.monthly.paymentInitiatedMayTake}
              </span>
            )}
            style={{ width: 'fit-content' }}
          >
            <IconButton className={classes.paidButton}>
              <CheckCircle fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    if (isRemoved) {
      return (
        <Box display="flex" alignItems="center">
          <Box className={cx(classes.root, classes.strikethrough)}>
            <Money value={amount} />
          </Box>
          <Tooltip
            arrow
            classes={{ tooltip: cx(classes.tooltip, classes.tooltipMargin) }}
            title={(
              <Typography style={{ fontWeight: 400 }} variant="body2" color="inherit">
                Late fee was removed from your payout
              </Typography>
            )}
          >
            <IconButton className={classes.tooltipMargin}>
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    return (
      <Box className={classes.root}>
        <Money value={amount} />
        <Box width="44px" />
      </Box>
    );
  }

  return null;
};
